import React, { useState, useEffect } from 'react';
import { Stage, Layer, Line, Rect, Text, Circle, Image as KonvaImage } from 'react-konva';

const SketchCanvasViewer = ({ page, width = 1200, height = 800 }) => {
  const [bgImage, setBgImage] = useState(null);

  useEffect(() => {
    if (!page?.backgroundImage) {
      setBgImage(null);
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setBgImage(img);
    img.src = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${page.backgroundImage}`;
  }, [page?.backgroundImage]);

  if (!page) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Nenhuma página selecionada
      </div>
    );
  }

  const allVisibleElements = page.layers
    ?.filter(layer => layer.visible !== false)
    ?.flatMap(layer => layer.elements || []) || [];

  const renderElement = (element, index) => {
    switch (element.type) {
      case 'rectangle':
        return (
          <React.Fragment key={element.id || index}>
            <Rect
              x={element.x}
              y={element.y}
              width={element.width}
              height={element.height}
              stroke={element.stroke || '#2563eb'}
              strokeWidth={element.strokeWidth || 2}
              fill={element.fill || 'transparent'}
            />
            {element.measurements && (
              <>
                <Text
                  text={`${Math.round(element.width)}cm`}
                  x={element.x + element.width / 2 - 20}
                  y={element.y - 25}
                  fontSize={12}
                  fill="#6b7280"
                  listening={false}
                />
                <Text
                  text={`${Math.round(element.height)}cm`}
                  x={element.x + element.width + 10}
                  y={element.y + element.height / 2 - 10}
                  fontSize={12}
                  fill="#6b7280"
                  listening={false}
                />
              </>
            )}
          </React.Fragment>
        );

      case 'line':
        const [x1, y1, x2, y2] = element.points || [];
        return (
          <React.Fragment key={element.id || index}>
            <Line
              points={element.points}
              stroke={element.color || '#2563eb'}
              strokeWidth={element.strokeWidth || 4}
              hitStrokeWidth={40}
            />
            {element.measurements?.length && (
              <Text
                text={`${Math.round(element.measurements.length)}cm`}
                x={(x1 + x2) / 2 - 25}
                y={(y1 + y2) / 2 - 25}
                fontSize={12}
                fill="#6b7280"
                listening={false}
              />
            )}
          </React.Fragment>
        );

      case 'freehand':
        return (
          <Line
            key={element.id || index}
            points={element.points}
            stroke={element.color || '#2563eb'}
            strokeWidth={element.strokeWidth || 2}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
          />
        );

      case 'text':
        return (
          <Text
            key={element.id || index}
            x={element.x}
            y={element.y}
            text={element.text}
            fontSize={element.fontSize || 16}
            fill={element.color || '#374151'}
          />
        );

      case 'circle':
        return (
          <Circle
            key={element.id || index}
            x={element.x}
            y={element.y}
            radius={element.radius}
            stroke={element.stroke || '#2563eb'}
            strokeWidth={element.strokeWidth || 2}
            fill={element.fill || 'transparent'}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Stage
      width={width}
      height={height}
      style={{ backgroundColor: '#f9fafb' }}
    >
      {bgImage && (
        <Layer>
          <KonvaImage
            image={bgImage}
            x={0}
            y={0}
            width={width}
            height={height}
          />
        </Layer>
      )}
      
      <Layer>
        {allVisibleElements.map((element, index) => renderElement(element, index))}
      </Layer>
    </Stage>
  );
};

export default SketchCanvasViewer;
